import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../hook';
import { Button, Card, Descriptions, Grid, Input, Result, Space, Spin, Switch, Tag, Typography } from 'antd';
import { appConfig } from '../../../common/config';
import CustomizedGoogleMap from '../../../components/google_map';
import { CheckCircleOutlined, EnvironmentOutlined, ExclamationCircleOutlined, InfoCircleOutlined, LoadingOutlined, SaveOutlined, WifiOutlined } from '@ant-design/icons';
import { changeAddress, changeCoordinate, fetchDevice, fetchRecentEvents, installDevice, setAssetName, setSyncMap, setEmptyDistance, setFullDistance } from './_slice';
import { convertCoordinateFromString, formatDateTime } from '../../../common/utils';
import { formatRecentEvents, isLevelSense, validateLevelDistances } from '../../../common/helper';

interface IDeviceInstallationProps {
  deviceIdOrSerial?: string;
  onSaved?: () => void;
  onCanceled?: () => void;
}

const DeviceInstallation: React.FC<IDeviceInstallationProps> = ({
  deviceIdOrSerial,
  onSaved,
  onCanceled,
}) => {
  const deviceState = useAppSelector((state) => state.device);
  const dispatch = useAppDispatch();
  const [keywords, setKeywords] = React.useState<string|undefined>(deviceIdOrSerial);
  const [deviceLocation, setDeviceLocation] = React.useState<string>('');

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const cardSize = isMobile ? 'small' : 'default';

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setDeviceLocation(`${position.coords.latitude},${position.coords.longitude}`);
      },
      (err) => {
        console.error("Failed to get location:", err);
      }
    );
  }, []);

  useEffect(() => {
    // If deviceIdOrSerial change, should change the keywords accordingly
    setKeywords(deviceIdOrSerial);
    dispatch(fetchDevice({deviceIdOrSerial}));

    // Polling recent events
    let isActive = true;
    const pollRecentEvents = async () => {
      if (isActive) {
        dispatch(fetchRecentEvents({secondsUntilNow: appConfig.settings.fetchRecentEventsMins * 60}));
        setTimeout(pollRecentEvents, 1500);
      }
    }
    pollRecentEvents();

    return () => {
      isActive = false;
    };

  }, [deviceIdOrSerial, dispatch]);

  const isLevelSenseDevice = Boolean(deviceState.device && isLevelSense(deviceState.device.DeviceTypeName));

  const isAddressError = !deviceState.address.trim();
  const isAssetNameError = !deviceState.assetName.trim();
  const isCoordinateError = !convertCoordinateFromString(deviceState.coordinate.trim());
  const isDistanceError = isLevelSenseDevice && !validateLevelDistances(deviceState.emptyDistance.trim(), deviceState.fullDistance.trim());

  return (
    <Space direction='vertical' size="middle" style={{ display: 'flex' }}>
      <Card size={cardSize} variant="borderless">
        <Input.Search
          size='large'
          placeholder="Device serial number or ID"
          value={keywords}
          loading={deviceState.isLoading}
          allowClear
          enterButton
          onSearch={(value) => {
            setKeywords(value);
            dispatch(fetchDevice({deviceIdOrSerial: value.trim()}));
          }}
          onChange={(e) => {
            setKeywords(e.target.value);
          }}
        />
      </Card>
      {
        keywords && !deviceState.isLoading && !deviceState.device && (
          <Result
            status="warning"
            title={appConfig.msgs.noDeviceFound}
          />
        )
      }

      {
        deviceState.device && (
          <>
            <Card size={cardSize} title={<><InfoCircleOutlined/> Device Info</>} variant="borderless">
              <Descriptions>
                <Descriptions.Item label="Customer ID">{deviceState.device.CustomerId}</Descriptions.Item>
                <Descriptions.Item label="Device ID">{deviceState.device.DeviceId}</Descriptions.Item>
                <Descriptions.Item label="Serial Number">{deviceState.device.SerialNumber}</Descriptions.Item>
                <Descriptions.Item label="Device Type">{deviceState.device.DeviceTypeName}</Descriptions.Item>
                <Descriptions.Item label="Network ID">
                  <Space>
                    <Tag color={appConfig.getNetworkType(deviceState.device.NetworkType).color} style={{ marginRight: 0 }}>{deviceState.device.NetworkType}</Tag>{deviceState.device.NetworkId}
                    {
                      deviceState.isLoadingSigfoxDevice && <Spin indicator={<LoadingOutlined spin />} size="small" />
                    }
                    {
                      deviceState.sigfoxDevice && <CheckCircleOutlined style={{ color: 'green' }} />
                    }
                    {
                      deviceState.sigfoxDeviceErrMsg && <ExclamationCircleOutlined style={{ color: 'red' }} />
                    }
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="Last Received">{formatDateTime(deviceState.device.LastReceived)}</Descriptions.Item>
              </Descriptions>
            </Card>

            <Card size={cardSize} title={<><WifiOutlined/> Check Signal Strength</>} variant="borderless">
              <Space direction='vertical' size="middle" style={{ display: 'flex' }}>
                <Typography.Text type="warning">{appConfig.msgs.testModeTip}</Typography.Text>
                <Input.TextArea rows={6} readOnly value={formatRecentEvents(deviceState.recentEvents)} />
              </Space>
            </Card>

            <Card size={cardSize}  title={<><EnvironmentOutlined/> Set Location</>} variant="borderless">
              <Space direction='vertical' size="middle" style={{ display: 'flex' }}>
                <Typography.Text type="warning">{appConfig.msgs.locationSearchTip}</Typography.Text>
                <CustomizedGoogleMap
                  position={deviceState.coordinate || deviceLocation}
                  onPositionChange={(value) => {
                    dispatch(changeCoordinate(value));
                  }}
                />
                <Input
                  addonBefore="Position"
                  value={deviceState.coordinate}
                  status={isCoordinateError ? 'error' : undefined}
                  placeholder="-43.123,172.456 (latitude,longitude)"
                  onChange={(e) => {
                    dispatch(changeCoordinate(e.target.value));
                  }}
                />
                <Input
                  addonBefore="Address"
                  value={deviceState.address}
                  status={isAddressError ? 'error' : undefined}
                  onChange={(e) => {
                    dispatch(changeAddress(e.target.value));
                  }}
                />
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <div>Sync Address to Map</div>
                  <Switch
                    checked={deviceState.isSyncMap}
                    checkedChildren="ON"
                    unCheckedChildren="OFF"
                    onChange={(isChecked) => {
                      dispatch(setSyncMap(isChecked));
                    }} 
                  />
                </div>
              </Space>
            </Card>
            <Card size={cardSize} title={<><InfoCircleOutlined/> Set Name</>} variant="borderless">
              <Space direction='vertical' size="middle" style={{ display: 'flex' }}>
                <Input
                  addonBefore="Asset Name"
                  value={deviceState.assetName}
                  status={isAssetNameError ? 'error' : undefined}
                  onChange={(e) => {
                    dispatch(setAssetName(e.target.value));
                  }}
                />
              </Space>
            </Card>
            
            {isLevelSenseDevice && (
              <Card size={cardSize} title={<><InfoCircleOutlined/> Set Container Size</>} variant="borderless">
                <Space direction='vertical' size="middle" style={{ display: 'flex' }}>
                  <Typography.Text type="warning">{appConfig.msgs.distanceTip}</Typography.Text>
                  <Input
                    addonBefore="Empty Distance (cm)"
                    value={deviceState.emptyDistance}
                    status={isDistanceError ? 'error' : undefined}
                    onChange={(e) => {
                      dispatch(setEmptyDistance(e.target.value));
                    }}
                  />
                  <Input
                    addonBefore="Full Distance (cm)"
                    value={deviceState.fullDistance}
                    status={isDistanceError ? 'error' : undefined}
                    onChange={(e) => {
                      dispatch(setFullDistance(e.target.value));
                    }}
                  />
                </Space>
              </Card>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              {
                onCanceled && (
                  <Button
                    size='large'
                    style={{ width: 160 }}
                    onClick={() => {
                      onCanceled();
                    }}
                  >
                    Cancel
                  </Button>
                )
              }
              <Button
                type="primary"
                icon={<SaveOutlined />}
                size='large'
                style={{ width: 160 }}
                disabled={isAddressError || isAssetNameError || isCoordinateError || isDistanceError}
                onClick={() => {
                  dispatch(installDevice());
                  if (onSaved) {
                    onSaved();
                  }
                }}
              >
                Install
              </Button>
            </div>
          </>
        )
      }
    </Space>
  );
};

export default DeviceInstallation;
